import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Bell, BellOff, Plus, Trash2, Mail, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';



const RATE_TYPES = [
  { value: 'fixed_3y', label: '3-Year Fixed' },
  { value: 'fixed_5y', label: '5-Year Fixed' },
  { value: 'variable_3y', label: '3-Year Variable' },
  { value: 'variable_5y', label: '5-Year Variable' }
];

const RateAlerts = ({ user, currentRates }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newAlert, setNewAlert] = useState({
    target_rate: '',
    rate_type: 'fixed_5y',
    notification_method: 'email',
    enabled: true
  });
  
  useEffect(() => {
    fetchAlerts();
  }, []);
  
  const fetchAlerts = async () => {
    try {
      const response = await api.get(`/api/rate-alerts`);
      setAlerts(response.data);
    } catch (error) {
      console.error('Error fetching alerts:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const createAlert = async () => {
    if (!newAlert.target_rate) {
      toast.error('Please enter a target rate');
      return;
    }
    
    try {
      await api.post(`/api/rate-alerts`, {
        ...newAlert,
        target_rate: parseFloat(newAlert.target_rate)
      });
      
      toast.success('Rate alert created! We\'ll notify you when rates drop.');
      setShowAddForm(false);
      setNewAlert({
        target_rate: '',
        rate_type: 'fixed_5y',
        notification_method: 'email',
        enabled: true
      });
      fetchAlerts();
    } catch (error) {
      console.error('Error creating alert:', error);
      toast.error('Failed to create alert');
    }
  };
  
  const deleteAlert = async (alertId) => {
    try {
      await api.delete(`/api/rate-alerts/${alertId}`);
      toast.success('Alert deleted');
      fetchAlerts();
    } catch (error) {
      console.error('Error deleting alert:', error);
      toast.error('Failed to delete alert');
    }
  };
  
  const toggleAlert = async (alert) => {
    try {
      await api.post(`/api/rate-alerts`, {
        ...alert,
        enabled: !alert.enabled
      });
      fetchAlerts();
    } catch (error) {
      console.error('Error toggling alert:', error);
    }
  };
  
  const getCurrentRateForType = (rateType) => {
    if (!currentRates?.rates?.length) return null;
    const bestRate = currentRates.rates[0];
    return bestRate?.[rateType];
  };
  
  const getRateTypeLabel = (rateType) => {
    return RATE_TYPES.find(rt => rt.value === rateType)?.label || rateType;
  };
  
  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }
  
  return (
    <div data-testid="rate-alerts" className="bg-card border border-border rounded-xl p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Bell className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-heading font-semibold">Rate Alerts</h3>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
        >
          <Plus className="h-4 w-4" />
          New Alert
        </button>
      </div>
      
      {/* Add Alert Form */}
      {showAddForm && (
        <div className="bg-muted/30 border border-border rounded-xl p-4 mb-6 space-y-4">
          <h4 className="text-sm font-semibold">Create Rate Alert</h4>
          <p className="text-xs text-muted-foreground">
            We'll email you when the rate drops to or below your target.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium mb-1.5 text-muted-foreground">Rate Type</label>
              <select
                value={newAlert.rate_type}
                onChange={(e) => setNewAlert({ ...newAlert, rate_type: e.target.value })}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary"
              >
                {RATE_TYPES.map(rt => (
                  <option key={rt.value} value={rt.value}>{rt.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-xs font-medium mb-1.5 text-muted-foreground">
                Target Rate (%)
                {getCurrentRateForType(newAlert.rate_type) && (
                  <span className="ml-2 text-primary">
                    Current: {getCurrentRateForType(newAlert.rate_type)}%
                  </span>
                )}
              </label>
              <input
                type="number"
                step="0.01"
                value={newAlert.target_rate}
                onChange={(e) => setNewAlert({ ...newAlert, target_rate: e.target.value })}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary"
                placeholder={getCurrentRateForType(newAlert.rate_type) ? 
                  `e.g., ${(getCurrentRateForType(newAlert.rate_type) - 0.25).toFixed(2)}` : 
                  'e.g., 3.50'}
              />
            </div>
            
            <div>
              <label className="block text-xs font-medium mb-1.5 text-muted-foreground">Notify Via</label>
              <select
                value={newAlert.notification_method}
                onChange={(e) => setNewAlert({ ...newAlert, notification_method: e.target.value })}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary"
              >
                <option value="email">Email</option>
              </select>
            </div>
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={createAlert}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm"
            >
              Create Alert
            </button>
            <button
              onClick={() => setShowAddForm(false)}
              className="px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      
      {/* Existing Alerts */}
      {alerts.length > 0 ? (
        <div className="space-y-3">
          {alerts.map((alert) => {
            const currentRate = getCurrentRateForType(alert.rate_type);
            const isTriggered = currentRate && currentRate <= alert.target_rate;
            
            return (
              <div 
                key={alert.alert_id}
                className={`flex items-center justify-between p-4 rounded-xl border transition-colors ${
                  isTriggered 
                    ? 'bg-green-500/10 border-green-500/20' 
                    : alert.enabled 
                      ? 'bg-muted/30 border-border' 
                      : 'bg-muted/10 border-border/50 opacity-60'
                }`}
              >
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => toggleAlert(alert)}
                    className={`p-2 rounded-lg transition-colors ${
                      alert.enabled ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {alert.enabled ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
                  </button>
                  
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{getRateTypeLabel(alert.rate_type)}</span>
                      {isTriggered && (
                        <span className="flex items-center gap-1 text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">
                          <CheckCircle className="h-3 w-3" />
                          Target Reached!
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                      <span>Target: <span className="font-mono font-medium text-foreground">{alert.target_rate}%</span></span>
                      {currentRate && (
                        <span>
                          Current: <span className={`font-mono font-medium ${
                            currentRate <= alert.target_rate ? 'text-green-400' : 'text-foreground'
                          }`}>{currentRate}%</span>
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        {user?.email?.slice(0, 20)}...
                      </span>
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={() => deleteAlert(alert.alert_id)}
                  className="p-2 text-muted-foreground hover:text-red-400 transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-8">
          <Bell className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground mb-2">No rate alerts set up yet</p>
          <p className="text-sm text-muted-foreground">
            Create an alert to get notified when rates drop to your target.
          </p>
        </div>
      )}
      
      {/* Info Box */}
      <div className="mt-6 p-4 bg-primary/5 border border-primary/10 rounded-xl">
        <div className="flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
          <div className="text-xs text-muted-foreground">
            <p className="font-medium text-foreground mb-1">How Rate Alerts Work</p>
            <p>We check market rates daily and send you an email when rates drop to or below your target. You can set multiple alerts for different rate types.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RateAlerts;
